import React, { useState, useEffect } from 'react';

const STUDENT_STATUSES = [
    { value: 'forwarded_to_university', label: 'Forwarded to university', color: '#581f37' },
    { value: 'forwarded_to_bos', label: 'Forwarded to BOS', color: '#6f42c1' },
    { value: 'bos_course_approved', label: 'BOS course approved', color: '#fd7e14' },
    { value: 'bos_course_rejected', label: 'BOS course rejected', color: '#dc3545' },
    { value: 'application_approved', label: 'Application approved', color: '#28a745' },
    { value: 'rejected_by_aca16', label: 'Rejected by ACA16', color: '#c0392b' },
];

const COURSE_STATUSES = [
    { value: 'verified', label: 'Verified', color: '#28a745' },
    { value: 'entered', label: 'Entered (not verified)', color: '#6c757d' },
];

// ─── Shared filter panel ──────────────────────────────────────────────────────
function FilterPanel({ statuses, exportAll, filterOpen, selectedStatuses, onToggleExportAll, onToggleFilter, onToggleStatus }) {
    const isInvalid = !exportAll && selectedStatuses.length === 0;

    return (
        <>
            {/* Export All row */}
            <div
                onClick={onToggleExportAll}
                style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 8,
                    border: `0.5px solid ${exportAll ? '#581f37' : '#dee2e6'}`,
                    background: exportAll ? 'rgba(88,31,55,0.04)' : 'var(--bs-tertiary-bg, #f8f9fa)',
                    cursor: 'pointer', marginBottom: 10,
                    transition: 'border-color 0.15s, background 0.15s',
                }}
            >
                <div style={{
                    width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                    background: exportAll ? '#581f37' : 'transparent',
                    border: `1.5px solid ${exportAll ? '#581f37' : '#adb5bd'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                }}>
                    {exportAll && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </div>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Export all</div>
                    <div style={{ fontSize: 11, color: 'var(--bs-secondary-color)', marginTop: 1 }}>
                        Include every record regardless of status
                    </div>
                </div>
            </div>

            {/* Filter toggle row */}
            <div
                onClick={onToggleFilter}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: filterOpen ? '8px 8px 0 0' : 8,
                    border: '0.5px solid #dee2e6',
                    background: 'var(--bs-tertiary-bg, #f8f9fa)',
                    cursor: 'pointer', userSelect: 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"
                        style={{ color: 'var(--bs-secondary-color)', opacity: 0.7 }}>
                        <path d="M1.5 1.5A.5.5 0 012 1h12a.5.5 0 01.354.854l-4.5 4.5A.5.5 0 019.5 6.75v4.793l-3-1.5V6.75a.5.5 0 00-.146-.354l-4.5-4.5A.5.5 0 011.5 1.5z" />
                    </svg>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--bs-secondary-color)' }}>
                        Filter by status
                    </span>
                    {!exportAll && selectedStatuses.length > 0 && (
                        <span style={{
                            fontSize: 10, fontWeight: 600, padding: '1px 7px',
                            borderRadius: 20, background: '#581f3720', color: '#581f37',
                        }}>
                            {selectedStatuses.length} selected
                        </span>
                    )}
                </div>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                    style={{ transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </div>

            {/* Status list */}
            {filterOpen && (
                <div style={{
                    border: '0.5px solid #dee2e6', borderTop: 'none',
                    borderRadius: '0 0 8px 8px', overflow: 'hidden',
                }}>
                    <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {statuses.map(s => {
                            const checked = selectedStatuses.includes(s.value);
                            return (
                                <div
                                    key={s.value}
                                    onClick={() => onToggleStatus(s.value)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                                        background: checked ? `${s.color}12` : 'transparent',
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    <div style={{
                                        width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                                        background: checked ? s.color : 'transparent',
                                        border: `1.5px solid ${checked ? s.color : '#adb5bd'}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.15s',
                                    }}>
                                        {checked && (
                                            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                                                <path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                                        <span style={{ fontSize: 13 }}>{s.label}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {isInvalid && (
                        <div style={{ padding: '7px 12px', borderTop: '0.5px solid #f8d7da', background: '#fff5f5' }}>
                            <span style={{ fontSize: 11, color: '#dc3545' }}>
                                Select at least one status to filter.
                            </span>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

// ─── Student Tab ──────────────────────────────────────────────────────────────
function StudentTab({ onExport, isExporting }) {
    const [exportAll, setExportAll] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [exportingType, setExportingType] = useState(null); // 'excel' | 'pdf' | null

    const toggleExportAll = () => {
        if (exportAll) return;
        setExportAll(true);
        setFilterOpen(false);
        setSelectedStatuses([]);
    };

    const toggleFilter = () => {
        const next = !filterOpen;
        setFilterOpen(next);
        if (next && exportAll) setExportAll(false);
        if (!next) setSelectedStatuses([]);
    };

    const toggleStatus = (value) =>
        setSelectedStatuses(prev =>
            prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
        );

    const isInvalid = !exportAll && selectedStatuses.length === 0;

    const handleExport = (type) => {
        if (isExporting || exportingType || isInvalid) return;
        setExportingType(type);
        onExport({ status: exportAll ? null : selectedStatuses, type });
    };

    // Clear local loading state when parent signals done
    useEffect(() => {
        if (!isExporting) setExportingType(null);
    }, [isExporting]);

    return (
        <div>
            <p style={{ fontSize: 12, color: 'var(--bs-secondary-color)', marginBottom: 12 }}>
                Export student credit applications. Optionally filter by status.
            </p>
            <FilterPanel
                statuses={STUDENT_STATUSES}
                exportAll={exportAll}
                filterOpen={filterOpen}
                selectedStatuses={selectedStatuses}
                onToggleExportAll={toggleExportAll}
                onToggleFilter={toggleFilter}
                onToggleStatus={toggleStatus}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button
                    type="button" onClick={() => handleExport('excel')}
                    disabled={isExporting || isInvalid}
                    className={`btn ${isInvalid ? 'btn-secondary' : 'btn-outline-success'} fw-bold flex-fill d-flex align-items-center justify-content-center gap-2`}
                    style={{ fontSize: 13, opacity: exportingType === 'pdf' ? 0.55 : 1 }}
                >
                    {exportingType === 'excel'
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        : <i className="bi bi-file-earmark-spreadsheet-fill" />}
                    {exportingType === 'excel' ? 'Exporting...' : 'Export Excel'}
                </button>
                <button
                    type="button" onClick={() => handleExport('pdf')}
                    disabled={isExporting || isInvalid}
                    className={`btn ${isInvalid ? 'btn-secondary' : 'btn-outline-danger'} fw-bold flex-fill d-flex align-items-center justify-content-center gap-2`}
                    style={{ fontSize: 13, opacity: exportingType === 'excel' ? 0.55 : 1 }}
                >
                    {exportingType === 'pdf'
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        : <i className="bi bi-file-earmark-pdf-fill" />}
                    {exportingType === 'pdf' ? 'Exporting...' : 'Export PDF'}
                </button>
            </div>
        </div>
    );
}

// ─── Courses Tab ──────────────────────────────────────────────────────────────
function CoursesTab({ onExport, isExporting }) {
    const [exportAll, setExportAll] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [exportingType, setExportingType] = useState(null); // 'excel' | 'pdf' | null

    const toggleExportAll = () => {
        if (exportAll) return;
        setExportAll(true);
        setFilterOpen(false);
        setSelectedStatuses([]);
    };

    const toggleFilter = () => {
        const next = !filterOpen;
        setFilterOpen(next);
        if (next && exportAll) setExportAll(false);
        if (!next) setSelectedStatuses([]);
    };

    const toggleStatus = (value) =>
        setSelectedStatuses(prev =>
            prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
        );

    const isInvalid = !exportAll && selectedStatuses.length === 0;

    const handleExport = (type) => {
        if (isExporting || exportingType || isInvalid) return;
        setExportingType(type);
        onExport({ status: exportAll ? null : selectedStatuses, type });
    };

    useEffect(() => {
        if (!isExporting) setExportingType(null);
    }, [isExporting]);

    return (
        <div>
            <p style={{ fontSize: 12, color: 'var(--bs-secondary-color)', marginBottom: 12 }}>
                Export external courses. Optionally filter by approval status.
            </p>
            <FilterPanel
                statuses={COURSE_STATUSES}
                exportAll={exportAll}
                filterOpen={filterOpen}
                selectedStatuses={selectedStatuses}
                onToggleExportAll={toggleExportAll}
                onToggleFilter={toggleFilter}
                onToggleStatus={toggleStatus}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button
                    type="button" onClick={() => handleExport('excel')}
                    disabled={isExporting || isInvalid}
                    className={`btn ${isInvalid ? 'btn-secondary' : 'btn-outline-success'} fw-bold flex-fill d-flex align-items-center justify-content-center gap-2`}
                    style={{ fontSize: 13, opacity: exportingType === 'pdf' ? 0.55 : 1 }}
                >
                    {exportingType === 'excel'
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        : <i className="bi bi-file-earmark-spreadsheet-fill" />}
                    {exportingType === 'excel' ? 'Exporting...' : 'Export Excel'}
                </button>
                <button
                    type="button" onClick={() => handleExport('pdf')}
                    disabled={isExporting || isInvalid}
                    className={`btn ${isInvalid ? 'btn-secondary' : 'btn-outline-danger'} fw-bold flex-fill d-flex align-items-center justify-content-center gap-2`}
                    style={{ fontSize: 13, opacity: exportingType === 'excel' ? 0.55 : 1 }}
                >
                    {exportingType === 'pdf'
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        : <i className="bi bi-file-earmark-pdf-fill" />}
                    {exportingType === 'pdf' ? 'Exporting...' : 'Export PDF'}
                </button>
            </div>
        </div>
    );
}

export default function ExportExternalCreditModal({
    show, onClose, onExportStudents, onExportCourses, isExporting,
    exportType = 'students', // ✅ 'students' | 'courses'
}) {
    const [activeTab, setActiveTab] = useState(exportType);

    useEffect(() => {
        if (show) setActiveTab(exportType); // ✅ sync on open
    }, [show, exportType]);

    if (!show) return null;

    // ✅ only show the tab matching exportType — no tab bar needed if single
    return (
        <>
            <div
                onClick={!isExporting ? onClose : undefined}
                className="modal-backdrop show"
                style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
            />
            <div className="modal d-block" tabIndex="-1" style={{ zIndex: 1055 }}>
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480, width: 'calc(100vw - 2rem)' }}>
                    <div className="modal-content border-0 shadow" style={{ borderRadius: 12, overflow: 'hidden' }}>

                        {/* Header */}
                        <div style={{ background: '#581f37', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                    width: 34, height: 34, borderRadius: 8,
                                    background: 'rgba(255,255,255,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className="bi bi-download text-white" style={{ fontSize: 15 }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 500, color: '#fff' }}>
                                        {exportType === 'students' ? 'Export student applications' : 'Export external courses'}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 1 }}>
                                        Choose format and filters
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button" onClick={!isExporting ? onClose : undefined} disabled={isExporting}
                                aria-label="Close"
                                style={{
                                    width: 28, height: 28, borderRadius: 6,
                                    background: 'rgba(255,255,255,0.1)', border: 'none',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <i className="bi bi-x-lg text-white" style={{ fontSize: 12 }} />
                            </button>
                        </div>

                        {/* Body — no tab bar, directly render based on exportType */}
                        <div style={{ padding: 18, maxHeight: '65vh', overflowY: 'auto' }}>
                            {exportType === 'students' && (
                                <StudentTab onExport={onExportStudents} isExporting={isExporting} />
                            )}
                            {exportType === 'courses' && (
                                <CoursesTab onExport={onExportCourses} isExporting={isExporting} />
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}